import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Centralizes page lifecycle signals (visibility, window focus) so components can
 * react consistently without scattering event listeners.
 */
@Injectable({ providedIn: 'root' })
export class PageLifecycleService {
  private backgroundSubject = new BehaviorSubject<boolean>(false);
  public background$: Observable<boolean> = this.backgroundSubject.asObservable();

  constructor() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const update = () => {
      this.backgroundSubject.next(this.computeBackgroundState());
    };

    document.addEventListener('visibilitychange', update, { passive: true });
    document.addEventListener('freeze', () => this.backgroundSubject.next(true));
    document.addEventListener('pagehide', () => this.backgroundSubject.next(true));
    window.addEventListener('blur', () => this.backgroundSubject.next(true));
    window.addEventListener('focus', update);
    window.addEventListener('online', update);
    window.addEventListener('pageshow', update);

    update();
  }

  private computeBackgroundState(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    const hidden = document.visibilityState === 'hidden';
    const hasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    return hidden || !hasFocus;
  }
}
